import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import {
  AlertOctagon,
  Boxes,
  RefreshCw,
  Plus,
  Search,
  Minus,
  Send,
  X,
  MapPin,
  ChevronRight,
} from "lucide-react";
import { formatLocalDateTime } from "../lib/constants";
import StockBadge from "../components/StockBadge";
import { useAuth } from "../contexts/AuthContext";
import { useDropdown } from "../lib/useAppConfig";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const REFRESH_MS = 60000;
const ITEM_TYPES_FALLBACK = ["Test Equipment", "Minor Material", "Consumable", "Tool"];

export default function LowInventory() {
  const nav = useNavigate();
  const { isRunnerLevel, isViewer } = useAuth();
  const ITEM_TYPES = useDropdown("item_type", ITEM_TYPES_FALLBACK);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("low"); // low | all
  const [type, setType] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({
    Item_Name: "",
    Item_Type: "Test Equipment",
    Current_Stock: 0,
    Low_Stock_Threshold: 0,
    Reorder_Point: 0,
    Location_in_Yard: "",
    Notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (tab === "low") params.low_only = true;
      if (type) params.type = type;
      if (q) params.q = q;
      const { data } = await api.get("/inventory", { params });
      setItems(data);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, type]);

  // Debounced reload on text query
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const adjust = async (id, delta) => {
    try {
      await api.post(`/inventory/${id}/adjust`, { delta });
      load();
    } catch {
      toast.error("Stock adjustment failed");
    }
  };

  const reorder = async (item) => {
    try {
      const { data } = await api.post(`/inventory/${item.id}/reorder`);
      toast.success(`Reorder request created — MR ${data.MR_Number}`);
      nav(`/shipments/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
    }
  };

  const createItem = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...draft,
        Current_Stock: Number(draft.Current_Stock || 0),
        Low_Stock_Threshold: Number(draft.Low_Stock_Threshold || 0),
        Reorder_Point: Number(draft.Reorder_Point || 0),
      };
      await api.post("/inventory", payload);
      toast.success(`Added ${payload.Item_Name}`);
      setCreateOpen(false);
      setDraft({
        Item_Name: "",
        Item_Type: "Test Equipment",
        Current_Stock: 0,
        Low_Stock_Threshold: 0,
        Reorder_Point: 0,
        Location_in_Yard: "",
        Notes: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700 flex items-center gap-2">
            <AlertOctagon className="h-4 w-4" />
            Yard Runner / Yard Inventory
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight flex items-center gap-2">
            <Boxes strokeWidth={2.5} className="h-7 w-7" />
            Low Inventory
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Items at or below their low-stock threshold. Tap "Reorder" to spawn a request automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="inventory-refresh-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {isRunnerLevel && (
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="inventory-add-btn"
            className="rg-tap px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide rounded-sm flex items-center gap-2 border-2 border-slate-900"
          >
            <Plus className="h-4 w-4" />
            Add item
          </button>
          )}
        </div>
      </div>

      {lastRefresh && (
        <div className="text-xs text-slate-500 mb-3">
          Last refreshed {formatLocalDateTime(lastRefresh.toISOString())} · {items.length} item
          {items.length === 1 ? "" : "s"}
        </div>
      )}

      {/* Tabs */}
      <div
        className="grid grid-cols-2 gap-1 p-1 bg-slate-100 border-2 border-slate-200 rounded-sm mb-3"
        data-testid="inventory-tabs"
      >
        {[
          { k: "low", label: "Low stock", testid: "inventory-tab-low" },
          { k: "all", label: "All items", testid: "inventory-tab-all" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={t.testid}
            className={`rg-tap rounded-sm font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2 ${
              tab === t.k ? "bg-slate-900 text-white" : "bg-transparent text-slate-700 hover:bg-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="inventory-search"
            placeholder="Search by name or yard location…"
            className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
          />
        </div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="inventory-type-filter"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
        >
          <option value="">All types</option>
          {ITEM_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading inventory…</div>
      ) : items.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="inventory-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">
            {tab === "low" ? "No low-stock items 🎉" : "No inventory items yet"}
          </div>
          <div className="text-sm text-slate-600">
            {tab === "low"
              ? "Everything is above its low-stock threshold."
              : "Add your first inventory item to start tracking stock."}
          </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="inventory-list">
          {items.map((item) => (
            <li
              key={item.id}
              data-testid={`inventory-row-${item.id}`}
              className={`rg-card p-4 sm:p-5 border-l-[6px] ${
                item.stock_level === "red"
                  ? "border-l-rose-700 bg-rose-50/40"
                  : item.stock_level === "yellow"
                  ? "border-l-amber-500 bg-amber-50/30"
                  : "border-l-emerald-600"
              }`}
            >
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="font-display text-lg uppercase text-slate-900">
                      {item.Item_Name}
                    </div>
                    <StockBadge
                      level={item.stock_level}
                      label={item.stock_level === "red" ? "LOW" : item.stock_level === "yellow" ? "REORDER" : "OK"}
                      testid={`inventory-level-${item.id}`}
                    />
                    <span className="rg-pill bg-slate-100 text-slate-600">{item.Item_Type}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                    <KV label="Stock">
                      <span className={`font-display text-2xl ${
                        item.stock_level === "red"
                          ? "text-rose-700"
                          : item.stock_level === "yellow"
                          ? "text-amber-700"
                          : "text-emerald-700"
                      }`}>
                        {item.Current_Stock}
                      </span>
                    </KV>
                    <KV label="Low ≤">
                      <span className="font-display text-2xl text-slate-700">
                        {item.Low_Stock_Threshold}
                      </span>
                    </KV>
                    <KV label="Reorder ≤">
                      <span className="font-display text-2xl text-slate-700">
                        {item.Reorder_Point}
                      </span>
                    </KV>
                    {item.Location_in_Yard && (
                      <KV label="Yard location">
                        <span className="text-sm font-bold flex items-center gap-1 text-slate-800">
                          <MapPin className="h-3.5 w-3.5 text-slate-500" />
                          {item.Location_in_Yard}
                        </span>
                      </KV>
                    )}
                  </div>
                  {item.Notes && (
                    <div className="text-xs text-slate-600 mt-2">{item.Notes}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {isRunnerLevel && (
                  <div className="flex items-center border-2 border-slate-300 rounded-sm" data-testid={`inventory-adjust-${item.id}`}>
                    <button
                      onClick={() => adjust(item.id, -1)}
                      data-testid={`inventory-decrement-${item.id}`}
                      className="rg-tap h-9 w-9 grid place-items-center hover:bg-slate-100 text-slate-700 border-r-2 border-slate-300"
                      title="Decrement"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => adjust(item.id, +1)}
                      data-testid={`inventory-increment-${item.id}`}
                      className="rg-tap h-9 w-9 grid place-items-center hover:bg-slate-100 text-slate-700"
                      title="Increment"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  )}
                  {item.stock_level === "red" && !isViewer && (
                    <button
                      onClick={() => reorder(item)}
                      data-testid={`inventory-reorder-${item.id}`}
                      className="rg-tap-xl px-4 bg-rose-700 hover:bg-rose-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-rose-900 text-sm"
                    >
                      <Send strokeWidth={2.5} className="h-4 w-4" />
                      Create Reorder
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Quick-create modal */}
      <AlertDialog open={createOpen} onOpenChange={setCreateOpen}>
        <AlertDialogContent data-testid="inventory-create-modal" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>New inventory item</AlertDialogTitle>
            <AlertDialogDescription>
              Adds a tracked item with stock thresholds. Low-stock alerts fire automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <Field label="Name *" className="sm:col-span-2">
              <input
                value={draft.Item_Name}
                onChange={(e) => setDraft({ ...draft, Item_Name: e.target.value })}
                data-testid="inventory-create-name"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                placeholder="e.g. Meggar"
              />
            </Field>
            <Field label="Type">
              <select
                value={draft.Item_Type}
                onChange={(e) => setDraft({ ...draft, Item_Type: e.target.value })}
                data-testid="inventory-create-type"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
              >
                {ITEM_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Yard location">
              <input
                value={draft.Location_in_Yard}
                onChange={(e) => setDraft({ ...draft, Location_in_Yard: e.target.value })}
                data-testid="inventory-create-location"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                placeholder="e.g. Bay A · Shelf 2"
              />
            </Field>
            <Field label="Current stock">
              <input
                type="number"
                min={0}
                value={draft.Current_Stock}
                onChange={(e) => setDraft({ ...draft, Current_Stock: e.target.value })}
                data-testid="inventory-create-stock"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </Field>
            <Field label="Low ≤">
              <input
                type="number"
                min={0}
                value={draft.Low_Stock_Threshold}
                onChange={(e) => setDraft({ ...draft, Low_Stock_Threshold: e.target.value })}
                data-testid="inventory-create-threshold"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </Field>
            <Field label="Reorder ≤" className="sm:col-span-2">
              <input
                type="number"
                min={0}
                value={draft.Reorder_Point}
                onChange={(e) => setDraft({ ...draft, Reorder_Point: e.target.value })}
                data-testid="inventory-create-reorder"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </Field>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inventory-create-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); createItem(); }}
              disabled={submitting || !draft.Item_Name.trim()}
              data-testid="inventory-create-submit"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {submitting ? "Adding…" : "Add item"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div>{children}</div>
    </div>
  );
}
